import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken, apiErrorMessage } from "@/lib/api";
import { getSocket } from "@/lib/socket";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = admin
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!getToken()) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await api.get("/admin/auth/me");
      setUser(data);
      // Upgrade the shared WebSocket to admin so this session receives
      // admin-scoped Hub events (`hub:admin:*`). Server verifies the
      // token; a non-admin token is rejected.
      try { getSocket().authenticateAdmin(getToken()); } catch { /* noop */ }
    } catch {
      setToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/admin/auth/login", { email, password });
      setToken(data.access_token);
      setUser(data.user);
      try { getSocket().authenticateAdmin(data.access_token); } catch { /* noop */ }
      return { ok: true };
    } catch (err) {
      return { ok: false, error: apiErrorMessage(err, "Invalid credentials") };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/admin/auth/logout"); } catch { /* ignore */ }
    setToken(null);
    setUser(false);
    // Drop the admin identity + reconnect so no post-logout tab keeps
    // receiving admin-scoped events on the old socket.
    try { getSocket().resetIdentity(); getSocket().reconnect(); } catch { /* noop */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, reload: load }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
